import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "components/basic/typography";
import AutoComplete from "components/basic/autoComplete";
import QuoteSection from "../quoteSection";
import { QuotePriceOption } from "../quoteTools";

interface QuoteCustomizationProps {
  chargerModelOptions: QuotePriceOption[];
  addOnOptions: QuotePriceOption[];
  selectedChargerModelId: number;
  selectedAddOnId: number;
  isChargerModelLocked: boolean;
  /** Render every field as read-only text (e.g. a saved quote detail view). */
  readOnly?: boolean;
  onChargerModelChange: (id: number) => void;
  onAddOnChange: (id: number) => void;
}

/**
 * CUSTOMIZATION section: lets the user change the charger model and add-on
 * service. Selecting a different option updates the estimate totals instantly.
 * When `readOnly` is set, both fields render as static text.
 */
const QuoteCustomization: React.FC<QuoteCustomizationProps> = ({
  chargerModelOptions,
  addOnOptions,
  selectedChargerModelId,
  selectedAddOnId,
  isChargerModelLocked,
  readOnly = false,
  onChargerModelChange,
  onAddOnChange,
}) => {
  const { t } = useTranslation("estimator");
  const theme = useTheme();

  const gridStyle: React.CSSProperties = {
    display: "grid",
    // One column on narrow screens, two on the full-width document. `min()`
    // keeps the track from overflowing containers narrower than 280px.
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
    gap: theme.spacing(3),
  };

  const fieldLabelStyle: React.CSSProperties = {
    marginBottom: theme.spacing(0.5),
    fontSize: "1em",
  };

  const helperTextStyle: React.CSSProperties = {
    marginTop: theme.spacing(1),
    opacity: 0.6,
  };

  const chargerModelIsReadOnly = readOnly || isChargerModelLocked;

  const selectedChargerModelLabel =
    chargerModelOptions.find((option) => option.id === selectedChargerModelId)
      ?.label ?? t("options.none");

  const selectedAddOnLabel =
    addOnOptions.find((option) => option.id === selectedAddOnId)?.label ??
    t("options.none");

  return (
    <QuoteSection title={t("quote.customization")}>
      <div style={gridStyle}>
        <div>
          <Typography variant="body1" style={fieldLabelStyle}>
            {t("quote.charger_selection")}
          </Typography>

          {chargerModelIsReadOnly ? (
            <Typography variant="body2">{selectedChargerModelLabel}</Typography>
          ) : (
            <AutoComplete
              name="chargerModel"
              size="tiny"
              value={selectedChargerModelId}
              options={chargerModelOptions}
              onChange={(newValue) => onChargerModelChange(newValue?.id ?? 0)}
            />
          )}
        </div>

        <div>
          <Typography variant="body1" style={fieldLabelStyle}>
            {t("quote.add_ons")}
          </Typography>

          {readOnly ? (
            <Typography variant="body2">{selectedAddOnLabel}</Typography>
          ) : (
            <AutoComplete
              name="addOns"
              size="tiny"
              value={selectedAddOnId}
              options={addOnOptions}
              onChange={(newValue) => onAddOnChange(newValue?.id ?? 0)}
            />
          )}
        </div>
      </div>

      {readOnly ? null : (
        <Typography variant="body2" style={helperTextStyle}>
          {t("quote.customize_hint")}
        </Typography>
      )}
    </QuoteSection>
  );
};

export default QuoteCustomization;
